import { InvoiceDetailsDto, InvoiceFormData } from "@/types";

const isFieldsInvoiceDetailsDto = (fields: InvoiceFormData | InvoiceDetailsDto): fields is InvoiceDetailsDto =>
    (fields as InvoiceDetailsDto).correspondentCosts !== undefined;

const convertFieldsToNumber = (fields: InvoiceDetailsDto): number => {
    const fieldsArray = [
        fields.correspondentCosts,
        fields.surveyorCosts,
        fields.expertCosts,
        fields.lawyerExpenses,
        fields.lawyerFees,
        fields.totalVatAmount,
        fields.totalLocalTax,
        fields.otherExpenses,
        fields.courtArbitFees,
        fields.counselFees,
        fields.claimSettlements,
        fields.advanceOfFunds
    ];

    const sum = fieldsArray.reduce((total, field) => {
        const value = typeof field === "number" ? field : convertCostToNumber(field);
        return (total || 0) + value;
    }, 0);

    // Subtract previousAdvanceOfFunds
    const previousAdvance =
        typeof fields.previousAdvanceOfFunds === "number"
            ? fields.previousAdvanceOfFunds
            : convertCostToNumber(fields.previousAdvanceOfFunds);

    return (sum || 0) - previousAdvance;
};

export const getTotalAmount = (fields: InvoiceFormData | InvoiceDetailsDto | undefined): number => {
    if (!fields) {
        return 0;
    }

    if (isFieldsInvoiceDetailsDto(fields)) {
        const fieldsAmount = convertFieldsToNumber(fields);
        return Number.isNaN(fieldsAmount) ? 0 : fieldsAmount;
    }

    const subPreviousAdvanceOfFunds: string = (-convertCostToNumber(fields.previousAdvanceOfFunds)).toString();

    const fieldsArray = [
        fields.correspondentCosts,
        fields.surveyorCosts,
        fields.expertCosts,
        fields.lawyerExpenses,
        fields.lawyerFees,
        fields.otherExpenses,
        fields.courtArbitFees,
        fields.counselFees,
        fields.totalVatAmount,
        fields.totalLocalTax,
        fields.advanceOfFunds,
        fields.claimSettlements,
        subPreviousAdvanceOfFunds
    ];

    return fieldsArray.reduce((sum, field) => sum + convertCostToNumber(field), 0);
};

export const convertCostToNumber = (field: string | undefined) => {
    if (!field) {
        return 0;
    }

    // Remove thousand separators (commas and spaces) and convert to number
    // Handles: "33,333", "1,234.56", "1 234.56"
    const cleaned = field
        .trim()
        .replaceAll(/,(?=\d{3}($|\D))/g, "") // Remove commas followed by 3 digits and end/non-digit
        .replaceAll(/\s/g, ""); // Remove all spaces

    const result = Number(cleaned);
    return Number.isNaN(result) ? 0 : result;
};
